// pages/SubjectsPageHome.jsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Grid,
  Card,
  CardContent,
  Skeleton,

  Avatar,

} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import PlayCircleRoundedIcon from "@mui/icons-material/PlayCircleRounded";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import { getSubjects } from "../utils/generalSubjectApi";

const PALETTES = [
  {
    gradient: "linear-gradient(135deg,#6366f1 0%,#8b5cf6 100%)",
    glow: "rgba(99,102,241,0.22)",
    accent: "#6366f1",
    light: "rgba(99,102,241,0.07)",
    text: "#4338ca",
  },
  {
    gradient: "linear-gradient(135deg,#06b6d4 0%,#0ea5e9 100%)",
    glow: "rgba(6,182,212,0.22)",
    accent: "#0ea5e9",
    light: "rgba(14,165,233,0.07)",
    text: "#0369a1",
  },
  {
    gradient: "linear-gradient(135deg,#10b981 0%,#059669 100%)",
    glow: "rgba(16,185,129,0.22)",
    accent: "#10b981",
    light: "rgba(16,185,129,0.07)",
    text: "#065f46",
  },
  {
    gradient: "linear-gradient(135deg,#f59e0b 0%,#d97706 100%)",
    glow: "rgba(245,158,11,0.22)",
    accent: "#f59e0b",
    light: "rgba(245,158,11,0.07)",
    text: "#92400e",
  },
  {
    gradient: "linear-gradient(135deg,#ec4899 0%,#db2777 100%)",
    glow: "rgba(236,72,153,0.22)",
    accent: "#ec4899",
    light: "rgba(236,72,153,0.07)",
    text: "#9d174d",
  },
  {
    gradient: "linear-gradient(135deg,#f97316 0%,#ea580c 100%)",
    glow: "rgba(249,115,22,0.22)",
    accent: "#f97316",
    light: "rgba(249,115,22,0.07)",
    text: "#9a3412",
  },
  {
    gradient: "linear-gradient(135deg,#8b5cf6 0%,#7c3aed 100%)",
    glow: "rgba(139,92,246,0.22)",
    accent: "#8b5cf6",
    light: "rgba(139,92,246,0.07)",
    text: "#5b21b6",
  },
  {
    gradient: "linear-gradient(135deg,#14b8a6 0%,#0d9488 100%)",
    glow: "rgba(20,184,166,0.22)",
    accent: "#14b8a6",
    light: "rgba(20,184,166,0.07)",
    text: "#115e59",
  },
];
const getPalette = (id) => PALETTES[id % PALETTES.length];
const getInitials = (name) =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const glass = {
  backdropFilter: "blur(24px) saturate(180%)",
  WebkitBackdropFilter: "blur(24px) saturate(180%)",
  background: "rgba(255,255,255,0.75)",
  border: "1px solid rgba(255,255,255,0.92)",
  boxShadow:
    "0 8px 32px rgba(15,23,42,0.07), inset 0 1px 0 rgba(255,255,255,0.95)",
};

const SubjectCard = ({ sub, index, navigate }) => {
  const p = getPalette(sub.id);
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.55,
        delay: index * 0.06,
        ease: [0.22, 1, 0.36, 1],
      }}
      whileHover={{ y: -8, transition: { duration: 0.25 } }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      style={{ height: "100%", cursor: "pointer" }}
      onClick={() => navigate(`/subjects/${sub.id}/videos`)}
    >
      <Card
        sx={{
          ...glass,
          height: "100%",
          borderRadius: "28px",
          overflow: "hidden",
          position: "relative",
          transition: "box-shadow 0.35s ease",
          boxShadow: hovered
            ? `0 28px 60px ${p.glow}, 0 0 0 1px ${p.accent}22, inset 0 1px 0 rgba(255,255,255,0.95)`
            : "0 8px 32px rgba(15,23,42,0.07), inset 0 1px 0 rgba(255,255,255,0.95)",
        }}
      >
        {/* gradient top strip */}
        <Box sx={{ height: 4, background: p.gradient }} />

        {/* decorative bg blob */}
        <Box
          sx={{
            position: "absolute",
            top: -30,
            right: -30,
            width: 130,
            height: 130,
            borderRadius: "50%",
            background: p.light,
            filter: "blur(20px)",
            pointerEvents: "none",
            transition: "opacity 0.3s",
            opacity: hovered ? 1 : 0.5,
          }}
        />

        <CardContent sx={{ p: 3.5, position: "relative" }}>
          {/* icon */}
          <Avatar
            sx={{
              width: 56,
              height: 56,
              mb: 2.5,
              background: p.gradient,
              boxShadow: hovered
                ? `0 10px 28px ${p.glow}`
                : `0 6px 16px ${p.glow}`,
              fontSize: "1rem",
              fontWeight: 800,
              fontFamily: "'Clash Display', sans-serif",
              transition: "box-shadow 0.3s",
            }}
          >
            {getInitials(sub.name)}
          </Avatar>

          <Typography
            sx={{
              fontFamily: "'Clash Display', sans-serif",
              fontWeight: 700,
              fontSize: "1.18rem",
              // color: "#0f172a",
              lineHeight: 1.3,
              textTransform: "capitalize",
              mb: 2,
              transition: "color 0.2s",
              color: hovered ? p.accent : "#0f172a",
            }}
          >
            {sub.name}
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.8,
                px: 1.5,
                py: 0.7,
                borderRadius: "10px",
                background: hovered ? p.light : "transparent",
                transition: "background 0.2s",
              }}
            >
              <PlayCircleRoundedIcon sx={{ fontSize: 15, color: p.accent }} />
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  color: p.text,
                  letterSpacing: "0.06em",
                }}
              >
                WATCH VIDEOS
              </Typography>
            </Box>
            <motion.div
              animate={{ x: hovered ? 4 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ArrowForwardRoundedIcon sx={{ color: p.accent, fontSize: 20 }} />
            </motion.div>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default function SubjectsPageHome() {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    getSubjects().then((d) => {
      setSubjects(d || []);
      setLoading(false);
    });
  }, []);

  const filtered = subjects.filter((s) =>
    s.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(160deg,#f8faff 0%,#f1f5ff 50%,#faf8ff 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* ambient orbs */}
      <Box
        sx={{
          position: "fixed",
          top: -200,
          left: -140,
          width: 600,
          height: 600,
          borderRadius: "50%",
          background:
            "radial-gradient(circle,rgba(99,102,241,0.11) 0%,transparent 70%)",
          filter: "blur(50px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "fixed",
          bottom: -160,
          right: -120,
          width: 500,
          height: 500,
          borderRadius: "50%",
          background:
            "radial-gradient(circle,rgba(16,185,129,0.09) 0%,transparent 70%)",
          filter: "blur(50px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "fixed",
          top: "40%",
          left: "60%",
          width: 340,
          height: 340,
          borderRadius: "50%",
          background:
            "radial-gradient(circle,rgba(245,158,11,0.07) 0%,transparent 70%)",
          filter: "blur(40px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          backgroundImage:
            "radial-gradient(rgba(99,102,241,0.05) 1px,transparent 1px)",
          backgroundSize: "26px 26px",
          pointerEvents: "none",
          zIndex: 0,
          maskImage:
            "radial-gradient(ellipse 80% 80% at 50% 50%,black 50%,transparent 100%)",
        }}
      />

      <Box
        sx={{
          maxWidth: 1360,
          mx: "auto",
          px: { xs: 2.5, sm: 4, md: 6 },
          pt: { xs: 5, md: 8 },
          pb: 12,
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* back */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Box
            onClick={() => {
              navigate("/home"); // fallback
            }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              mb: 5,
              px: 2.5,
              py: 1.2,
              borderRadius: "14px",
              cursor: "pointer",
              ...glass,
              fontWeight: 600,
              color: "#475569",
              fontSize: "0.88rem",
              "&:hover": { color: "#6366f1" },
              transition: "color 0.2s",
            }}
          >
            <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />
            Home Page
          </Box>
        </motion.div>

        {/* hero */}
        <Box sx={{ textAlign: "center", mb: { xs: 6, md: 9 } }}>
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 2.5,
                py: 1,
                borderRadius: "100px",
                ...glass,
                mb: 3,
              }}
            >
              <SchoolRoundedIcon sx={{ fontSize: 16, color: "#6366f1" }} />
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  color: "#6366f1",
                  letterSpacing: "0.08em",
                }}
              >
                LEARNING PLATFORM
              </Typography>
            </Box>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <Typography
              sx={{
                fontFamily: "'Clash Display', sans-serif",
                fontWeight: 700,
                fontSize: { xs: "2.4rem", sm: "3.2rem", md: "4rem" },
                lineHeight: 1.08,
                letterSpacing: "-0.05em",
                color: "#0f172a",
                mb: 2,
              }}
            >
              Explore{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(90deg,#6366f1,#8b5cf6,#06b6d4)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Subjects
              </Box>
            </Typography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Typography
              sx={{
                color: "#64748b",
                fontSize: { xs: "1rem", md: "1.12rem" },
                maxWidth: 500,
                mx: "auto",
                lineHeight: 1.7,
              }}
            >
              Dive into curated video lessons. Pick a subject and start learning
              today.
            </Typography>
          </motion.div>

          {/* search */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Box sx={{ mt: 5, maxWidth: 480, mx: "auto" }}>
              <TextField
                fullWidth
                placeholder="Search subjects…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "18px",
                    background: "rgba(255,255,255,0.82)",
                    backdropFilter: "blur(16px)",
                    boxShadow: "0 4px 24px rgba(99,102,241,0.1)",
                    "& fieldset": { borderColor: "rgba(99,102,241,0.18)" },
                    "&:hover fieldset": { borderColor: "rgba(99,102,241,0.4)" },
                    "&.Mui-focused fieldset": {
                      borderColor: "#6366f1",
                      borderWidth: 1.5,
                    },
                    "& input": { fontSize: "0.95rem", py: 1.8 },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon sx={{ color: "#94a3b8" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </motion.div>

          {!loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <Chip
                label={`${filtered.length} subject${
                  filtered.length !== 1 ? "s" : ""
                }`}
                sx={{
                  mt: 3,
                  background: "rgba(99,102,241,0.1)",
                  color: "#6366f1",
                  fontWeight: 700,
                  border: "1px solid rgba(99,102,241,0.2)",
                  borderRadius: "10px",
                }}
              />
            </motion.div>
          )}
        </Box>

        {/* grid */}
        <AnimatePresence mode="wait">
          {loading ? (
            <Grid container spacing={3.5}>
              {[...Array(8)].map((_, i) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                  <Card sx={{ ...glass, borderRadius: "28px", height: 200 }}>
                    <Box
                      sx={{
                        height: 4,
                        background: "linear-gradient(90deg,#e2e8f0,#f1f5f9)",
                      }}
                    />
                    <CardContent sx={{ p: 3.5 }}>
                      <Skeleton
                        variant="circular"
                        width={56}
                        height={56}
                        sx={{ mb: 2.5 }}
                      />
                      <Skeleton variant="text" width="75%" height={28} />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : filtered.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{ textAlign: "center", paddingTop: 60 }}
            >
              <Typography
                sx={{
                  fontFamily: "'Clash Display', sans-serif",
                  fontSize: "1.5rem",
                  fontWeight: 700,
                  color: "#0f172a",
                  mb: 1,
                }}
              >
                No subjects found
              </Typography>
              <Typography sx={{ color: "#64748b" }}>
                Try a different search term
              </Typography>
            </motion.div>
          ) : (
            <Grid container spacing={3.5}>
              {filtered.map((sub, i) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={sub.id}>
                  <SubjectCard sub={sub} index={i} navigate={navigate} />
                </Grid>
              ))}
            </Grid>
          )}
        </AnimatePresence>
      </Box>
    </Box>
  );
}
